/**
 * SOLID Review
 * S — Strong. One concern: the pending code — looked up, routed, the seat taken.
 * O — Strong. Where a code leads is the Domain's routing decision; a new route is one more branch here.
 * L — Strong. Implements its entry-step role exactly.
 * I — Strong. `take`.
 * D — Strong. The session's deps and the seat restore, injected.
 */
import type { IContextRequest } from '../ports/IContextRequest';
import type { ICommandNothing } from '../../domain/ports/schemas/TableMoves';
import type { ICommandJoinRouted, ICommandTableJoined, ICommandTableRejoined, ICommandTableLateJoined } from '../../domain/ports/schemas/SessionMoves';
import type { IPayloadServerRoomCode } from '../ports/contracts';
import type { IConfigDepSession } from './IConfigDepSession';
import type { IServiceEntryStep } from './SessionEntryRoute';
import type { SeatRestore } from './SeatRestore';

const NOTHING: ICommandNothing = {};

/**
 * THE PENDING CODE (FE2's smart-join routing): look it up, route it, take the seat. Answers whether the join SETTLED — the
 * routing decision and the seat's ran — so the route does not ask again for a code nothing has consumed. The code stays
 * pending until a decision consumes it, so after a failed step the intent would be `join` again — and was, until the
 * request's deadline (frontend strict-ports FE9, §28.206: a tight loop with no I/O froze the tab; with the lookup in it, it
 * hammered the server).
 */
export class JoinByCodeStep implements IServiceEntryStep {
  constructor(private readonly deps: IConfigDepSession, private readonly seats: SeatRestore) {}

  async take(ctx: IContextRequest): Promise<boolean> {
    const { decisions, step, rooms } = this.deps;
    const plan = await step.run(ctx, (input) => decisions.planJoin(ctx, input, NOTHING));
    if (!plan.success || plan.value.code === null) return false;
    const code = plan.value.code;
    const roomCode: IPayloadServerRoomCode = { roomCode: code };
    const found = await rooms.getRoomByCode(ctx, roomCode);
    const looked: ICommandJoinRouted = { code, lookup: found.success ? found.value : null, failed: !found.success };
    const routed = await step.run(ctx, (input) => decisions.joinRouted(ctx, input, looked));
    if (!routed.success) return false;
    const route = routed.value.route;
    if (route.type === 'JOIN') {
      const joined = await rooms.joinRoom(ctx, route.request);
      const answer: ICommandTableJoined = { request: route.request, room: joined.success ? joined.value : null };
      return (await step.run(ctx, (input) => decisions.tableJoined(ctx, input, answer))).success;
    }
    if (route.type === 'REJOIN') {
      const rejoined = await rooms.rejoinRoomByCode(ctx, route.request);
      const answer: ICommandTableRejoined = { request: route.request, rejoin: rejoined.success ? rejoined.value : null };
      const seated = await step.run(ctx, (input) => decisions.tableRejoined(ctx, input, answer));
      if (seated.success && seated.value.read !== null) await this.seats.restoreRound(ctx, seated.value.read);
      return seated.success;
    }
    if (route.type === 'LATE_JOIN') {
      const joined = await rooms.lateJoinRoom(ctx, route.request);
      const answer: ICommandTableLateJoined = { request: route.request, room: joined.success ? joined.value : null };
      const seated = await step.run(ctx, (input) => decisions.tableLateJoined(ctx, input, answer));
      if (seated.success && seated.value.read !== null) await this.seats.restoreRound(ctx, seated.value.read);
      return seated.success;
    }
    // Stopped — no such room, a full one, an unreachable server: the routing decision consumed the code.
    return true;
  }
}
